import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { recruitmentApi } from "../../services/recruitmentApi";
import { interviewSchedulingApi } from "../../services/interviewSchedulingApi";
import { formatApiErrorDetail } from "../../lib/api";
import { formatJst } from "../../lib/date";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { NotFoundState } from "../../components/NotFoundState";
import { usePermissions } from "../../hooks/usePermissions";
import { Section } from "../../components/recruitment/Section";
import { ApplicantActionPanel } from "../../components/recruitment/ApplicantActionPanel";
import { ApplicantInterviewHistory } from "../../components/recruitment/ApplicantInterviewHistory";
import { needsAvailabilityReview } from "../../lib/applicantMatching";
import { WEEKLY_DAYS, WEEKLY_PERIODS } from "../../constants/interviewScheduling";
import { useLocalization } from "../../hooks/useLocalization";

const ACTIVE_BOOKING_STATUSES = ["HELD", "PENDING_CONFIRMATION", "CONFIRMED", "RESCHEDULE_REQUESTED"];

function pickActiveBooking(bookings) {
  const active = bookings.filter((b) => ACTIVE_BOOKING_STATUSES.includes(b.status));
  if (active.length === 0) return null;
  return active.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];
}

function interviewOutcomeLabel(bookings, t) {
  if (!bookings || bookings.length === 0) return t("mgmtRecruitment.noInterviewHeld");
  const sorted = [...bookings].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const latest = sorted[0];
  if (latest.status === "COMPLETED") return `${t("mgmtRecruitment.completedOn")} ${latest.date}`;
  if (latest.status === "NO_SHOW") return `${t("mgmtRecruitment.noShowOn")} ${latest.date}`;
  if (latest.status === "CONFIRMED") return `${t("mgmtRecruitment.confirmedFor")} ${latest.date} (${t("mgmtRecruitment.outcomeNotYetRecorded")})`;
  return `${latest.status.replaceAll("_", " ")} - ${t("mgmtRecruitment.outcomeNotRecorded")}`;
}

export default function RecruitmentApplicantDetailPage() {
  const { applicantId } = useParams();
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { canManageRecruitment, canManageInterviews } = usePermissions();
  const [applicant, setApplicant] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [job, setJob] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [error, setError] = useState("");

  const load = async () => {
    const { data } = await recruitmentApi.getApplicant(applicantId);
    setApplicant(data);
    setNotFound(false);
    const jobResp = await recruitmentApi.getJob(data.job_posting_id);
    setJob(jobResp.data);
    if (canManageInterviews) {
      const bookingResp = await interviewSchedulingApi.listBookings({ applicant_id: applicantId });
      setBookings(bookingResp.data);
    }
  };

  useEffect(() => {
    if (!canManageRecruitment) return;
    load().catch((err) => {
      if (err.response?.status === 404) setNotFound(true);
      else setError(t("mgmtRecruitment.failedToLoadApplicant"));
    });
  }, [applicantId, canManageRecruitment]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn, msg) => {
    setError("");
    try {
      await fn();
      toast.success(msg);
      await load();
      return true;
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      setError(detail);
      toast.error(detail);
      return false;
    }
  };

  const downloadResume = async () => {
    try {
      const { data } = await recruitmentApi.downloadResume(applicantId);
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url; a.download = applicant.resume_filename || "resume";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!canManageRecruitment) return <p className="text-sm text-gray-500" data-testid="recruitment-applicant-detail-access-denied">{t("common.accessDenied")}</p>;
  if (notFound) return <NotFoundState testId="applicant-not-found" backTo="/management/recruitment/applicants" />;
  if (!applicant) return <p className="text-sm text-gray-500" data-testid="applicant-detail-loading">{t("common.loading")}</p>;

  const activeBooking = pickActiveBooking(bookings);

  return (
    <div className="max-w-6xl space-y-5" data-testid="recruitment-applicant-detail-page">
      <PageHelpBanner pageKey="recruitment-applicant-detail" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtRecruitment.applicantDetailHelp")}</p>
      </PageHelpBanner>
      <button onClick={() => navigate("/management/recruitment/applicants")} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700" data-testid="back-to-applicants-link">
        <ArrowLeft size={15} /> {t("mgmtRecruitment.backToApplicants")}
      </button>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900" data-testid="applicant-legal-name">{applicant.legal_name}</h2>
          <p className="text-sm text-gray-500">{t("mgmtRecruitment.appliedFor")} {job?.title_en || applicant.job_posting_id}</p>
        </div>
        <StatusBadge status={applicant.status} />
      </div>

      {error && <p className="text-sm text-red-500" data-testid="applicant-detail-error">{error}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5 items-start">
        <div className="space-y-5 min-w-0">
          <Section title={t("mgmtRecruitment.contactAndAnswers")}>
            <div className="space-y-1.5">
              <p className="text-sm text-gray-700">{applicant.email}</p>
              <p className="text-sm text-gray-700">{applicant.phone}</p>
              <p className="text-sm text-gray-600 pt-1"><span className="text-gray-500">{t("mgmtRecruitment.workAuthStatus")}:</span> {(applicant.work_authorization_status || t("mgmtRecruitment.notSpecified")).replaceAll("_", " ")}</p>
              {applicant.work_authorization_other_detail && <p className="text-sm text-gray-600"><span className="text-gray-500">{t("mgmtRecruitment.detail")}:</span> {applicant.work_authorization_other_detail}</p>}
              {applicant.notes && <p className="text-sm text-gray-500 mt-2 italic" data-testid="applicant-notes">"{applicant.notes}"</p>}
            </div>
          </Section>

          <Section title={t("mgmtRecruitment.shiftAndAvailability")}>
            <div className="space-y-3" data-testid="applicant-matching-summary">
              <div className="flex flex-wrap gap-1.5">
                {(applicant.shift_interests || []).length === 0 && <span className="text-xs text-gray-500">{t("mgmtRecruitment.noShiftInterests")}</span>}
                {(applicant.shift_interests || []).map((s) => (
                  <span key={s} className="px-2.5 py-1 rounded-full text-xs font-semibold border bg-gray-50 border-gray-200 text-gray-600">{s.replaceAll("_", " ")}</span>
                ))}
              </div>
              <p className="text-sm text-gray-600" data-testid="applicant-weekly-availability-summary">
                <span className="text-gray-500">{t("mgmtRecruitment.weeklyAvailability")}:</span> {applicant.shifts_per_week ? `${applicant.shifts_per_week} ${t("mgmtRecruitment.shiftsPerWeek")}` : t("mgmtRecruitment.notSpecified")}
                {applicant.can_work_min_shifts && ` \u00b7 ${t("mgmtRecruitment.canCommitToMinShifts")}: ${applicant.can_work_min_shifts}`}
              </p>

              <div className="overflow-x-auto">
                <table className="text-xs border border-gray-200 rounded-lg" data-testid="applicant-weekly-availability-grid">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1 text-left font-medium text-gray-500">{t("mgmtRecruitment.colDay")}</th>
                      {WEEKLY_PERIODS.map((p) => <th key={p} className="px-2 py-1 font-medium text-gray-500 capitalize">{p}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {WEEKLY_DAYS.map((day) => (
                      <tr key={day} className="border-t border-gray-100">
                        <td className="px-2 py-1 font-semibold text-gray-600">{day.slice(0, 3)}</td>
                        {WEEKLY_PERIODS.map((p) => (
                          <td key={p} className="px-2 py-1 text-center">
                            {applicant.weekly_availability?.[day]?.[p] ? <span className="text-emerald-600">{"\u2713"}</span> : <span className="text-gray-200">{"\u00b7"}</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-sm text-gray-600"><span className="text-gray-500">{t("mgmtRecruitment.earliestStartDate")}:</span> {applicant.earliest_start_date || t("mgmtRecruitment.notSpecified")}</p>
              {applicant.scheduling_restrictions && <p className="text-sm text-gray-600"><span className="text-gray-500">{t("mgmtRecruitment.schedulingRestrictions")}:</span> {applicant.scheduling_restrictions}</p>}
              <p className="text-sm text-gray-600"><span className="text-gray-500">{t("mgmtRecruitment.premiumTermsAck")}:</span> {applicant.shift_premium_ack ? t("common.yes") : t("mgmtRecruitment.notApplicable")}</p>

              {needsAvailabilityReview(applicant) && (
                <p className="text-xs font-semibold text-amber-600" data-testid="applicant-availability-review-flag">{t("mgmtRecruitment.availabilityReviewFlag")}</p>
              )}

              <div className="pt-2 border-t border-gray-100 space-y-1">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t("mgmtRecruitment.potentialShiftMatch")}</p>
                <p className="text-sm text-gray-700" data-testid="applicant-opening-match">
                  {t("mgmtRecruitment.openingShiftHours")}: {(applicant.shift_interests || []).includes("OPENING") ? <span className="text-emerald-600 font-semibold">{t("mgmtRecruitment.interested")}</span> : <span className="text-gray-500">{t("mgmtRecruitment.notIndicated")}</span>}
                </p>
                <p className="text-sm text-gray-700" data-testid="applicant-closing-match">
                  {t("mgmtRecruitment.closingShiftHours")}: {(applicant.shift_interests || []).includes("CLOSING") ? <span className="text-emerald-600 font-semibold">{t("mgmtRecruitment.interested")}</span> : <span className="text-gray-500">{t("mgmtRecruitment.notIndicated")}</span>}
                </p>
                <p className="text-[11px] text-gray-500 pt-1">{t("mgmtRecruitment.matchSignalDisclaimer")}</p>
              </div>
            </div>
          </Section>

          <Section title={t("mgmtRecruitment.resume")}>
            {applicant.resume_filename ? (
              <div className="space-y-1" data-testid="resume-metadata-panel">
                <p className="text-sm text-gray-700 flex items-center gap-1.5"><FileText size={14} /> {applicant.resume_filename}</p>
                <p className="text-xs text-gray-500">{t("mgmtRecruitment.uploaded")}: {applicant.resume_uploaded_at ? formatJst(applicant.resume_uploaded_at) : "-"} \u00b7 {t("mgmtRecruitment.size")}: {applicant.resume_size ? `${Math.round(applicant.resume_size / 1024)} KB` : "-"}</p>
                <p className="text-xs text-gray-500">{t("mgmtRecruitment.scanStatus")}: {applicant.resume_scan_status || "-"}</p>
                {applicant.resume_audit?.some((a) => a.duplicate_of_applicant_id) && (
                  <p className="text-xs text-amber-600" data-testid="resume-duplicate-warning">{t("mgmtRecruitment.duplicateResumeWarning")}</p>
                )}
                <button data-testid="download-resume-btn" onClick={downloadResume} className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-semibold hover:bg-gray-800">
                  <Download size={13} /> {t("mgmtRecruitment.downloadResume")}
                </button>
              </div>
            ) : (
              <p className="text-xs text-gray-500" data-testid="no-resume-on-file">{t("mgmtRecruitment.noResumeOnFile")}</p>
            )}
          </Section>

          <Section title={t("mgmtRecruitment.interviewHistory")}>
            <ApplicantInterviewHistory bookings={bookings} />
          </Section>

          <Section title={t("mgmtRecruitment.applicationTimeline")}>
            <div className="space-y-1.5 text-sm text-gray-600" data-testid="applicant-timeline">
              <p><span className="text-gray-500">{t("mgmtRecruitment.applied")}:</span> {formatJst(applicant.created_at)}</p>
              {applicant.reviewed_at && <p><span className="text-gray-500">{t("mgmtRecruitment.lastReviewed")}:</span> {formatJst(applicant.reviewed_at)}</p>}
              {applicant.rejected_reason && <p className="text-red-600"><span className="text-gray-500">{t("mgmtRecruitment.rejected")}:</span> {applicant.rejected_reason}</p>}
              {applicant.converted_application_id && <p className="text-emerald-700"><span className="text-gray-500">{t("mgmtRecruitment.convertedTo")}:</span> {t("mgmtRecruitment.employeeApplication")} {applicant.converted_application_id}</p>}
            </div>
          </Section>
        </div>

        <div className="lg:sticky lg:top-4">
          <ApplicantActionPanel
            applicant={applicant}
            job={job}
            activeBooking={activeBooking}
            canManageInterviews={canManageInterviews}
            interviewOutcomeLabel={interviewOutcomeLabel(bookings, t)}
            onAdvance={() => act(() => {
              const ADVANCE_MAP = { NEW: "SCREENING", SCREENING: "INTERVIEWED", INTERVIEW_SCHEDULED: "INTERVIEWED", INTERVIEWED: "OFFER_EXTENDED", OFFER_EXTENDED: "HIRED" };
              return recruitmentApi.setApplicantStatus(applicantId, ADVANCE_MAP[applicant.status]);
            }, t("mgmtRecruitment.applicantAdvanced"))}
            onReject={(reason) => act(() => recruitmentApi.setApplicantStatus(applicantId, "REJECTED", reason), t("mgmtRecruitment.applicantRejected"))}
            onWithdraw={() => act(() => recruitmentApi.setApplicantStatus(applicantId, "WITHDRAWN"), t("mgmtRecruitment.applicantWithdrawn"))}
            onConfirmInterview={() => act(() => interviewSchedulingApi.confirmBooking(activeBooking.id), t("mgmtRecruitment.interviewConfirmed"))}
            onRescheduleInterview={(date, start_time) => act(() => interviewSchedulingApi.rescheduleBooking(activeBooking.id, { date, start_time }), t("mgmtRecruitment.interviewRescheduled"))}
            onProposeTime={(date, start_time) => act(() => interviewSchedulingApi.proposeTime(activeBooking.id, { date, start_time }), t("mgmtRecruitment.newTimeProposed"))}
            onCancelInterview={(reason) => act(() => interviewSchedulingApi.cancelBooking(activeBooking.id, reason), t("mgmtRecruitment.interviewCancelled"))}
            onRecordComplete={() => act(() => interviewSchedulingApi.markCompleted(activeBooking.id), t("mgmtRecruitment.interviewMarkedCompleted"))}
            onRecordNoShow={() => act(() => interviewSchedulingApi.markNoShow(activeBooking.id), t("mgmtRecruitment.interviewMarkedNoShow"))}
            onConvert={() => act(() => recruitmentApi.convertToApplication(applicantId), t("mgmtRecruitment.convertedToEmployeeApplication"))}
            onAddNote={(note) => act(() => recruitmentApi.addApplicantNote(applicantId, note), t("mgmtRecruitment.noteAdded"))}
          />
        </div>
      </div>
    </div>
  );
}
